import useSocketReconnect from '../../utils/useSocketReconnect';
import React, { useState, useEffect } from 'react';
import './MerxandatgeNotifications.css';
import { can } from '../../utils/permissions';
import { getCastellerDisplayName } from '../../utils/castellerName';

function MerxandatgeNotifications({ socket, userInfo }) {
    useSocketReconnect(socket, () => {
        if (!isAdmin) return;
        socket.emit('.get_merxandatge_notification_recipients');
        socket.emit('.get_merxandatge_potential_recipients');
    });

  const [recipients, setRecipients] = useState([]);
  const [potentialRecipients, setPotentialRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState({ message: '', type: '' });

  const isAdmin = can(userInfo, 'MANAGE_MERCHANDISE');

  useEffect(() => {
    if (!isAdmin) return;

    // Load current notification recipients
    socket.emit('.get_merxandatge_notification_recipients');
    socket.emit('.get_merxandatge_potential_recipients');

    const handleRecipients = (data) => {
      setRecipients(data.recipients || []);
      setLoading(false);
    };

    const handlePotentialRecipients = (data) => {
      setPotentialRecipients(data.potentialRecipients || []);
      setLoading(false);
    };

    const handleRecipientsUpdated = () => {
      // Refresh lists when recipients are updated
      socket.emit('.get_merxandatge_notification_recipients');
      socket.emit('.get_merxandatge_potential_recipients');
    };

    socket.on('.merxandatge_notification_recipients', handleRecipients);
    socket.on('.merxandatge_potential_recipients', handlePotentialRecipients);
    socket.on('.merxandatge_notification_recipients_updated', handleRecipientsUpdated);

    return () => {
      socket.off('.merxandatge_notification_recipients', handleRecipients);
      socket.off('.merxandatge_potential_recipients', handlePotentialRecipients);
      socket.off('.merxandatge_notification_recipients_updated', handleRecipientsUpdated);
    };
  }, [socket, isAdmin]);

  const toggleRecipient = (user, isCurrentRecipient) => {
    if (loading) return;
    setLoading(true);
    
    if (isCurrentRecipient) {
      // Remove recipient
      socket.emit('.remove_merxandatge_notification_recipient', user.id || user.user_id, (response) => {
        setLoading(false);
        
        if (response.success) {
          setSaveStatus({
            message: 'Destinatari eliminat correctament',
            type: 'success'
          });
        } else {
          setSaveStatus({
            message: response.message || 'Error eliminant destinatari',
            type: 'error'
          });
        }
        
        // Clear status message after 3 seconds
        setTimeout(() => setSaveStatus({ message: '', type: '' }), 3000);
      });
    } else {
      // Add recipient
      socket.emit('.add_merxandatge_notification_recipient', user.id, (response) => {
        setLoading(false);
        
        if (response.success) {
          setSaveStatus({
            message: 'Destinatari afegit correctament',
            type: 'success'
          });
        } else {
          setSaveStatus({
            message: response.message || 'Error afegint destinatari',
            type: 'error'
          });
        }
        
        // Clear status message after 3 seconds
        setTimeout(() => setSaveStatus({ message: '', type: '' }), 3000);
      });
    }
  };

  const getDisplayName = (user) => {
    if (!user) return '';
    return getCastellerDisplayName(user, `Usuari ${user.id}`);
  };

  // Create a merged list of all administrators
  const getAllAdmins = () => {
    const recipientIds = new Set(recipients.map(r => r.user_id));
    
    // First add current recipients
    const allAdmins = [...recipients.map(r => ({
      ...r,
      id: r.user_id,
      isRecipient: true
    }))];
    
    // Then add potential recipients that aren't already in the list
    potentialRecipients.forEach(admin => {
      if (!recipientIds.has(admin.id)) {
        allAdmins.push({
          ...admin,
          isRecipient: admin.is_recipient === 1
        });
      }
    });
    
    // Sort by recipient status (recipients first) then by name
    return allAdmins.sort((a, b) => {
      if (a.isRecipient !== b.isRecipient) {
        return a.isRecipient ? -1 : 1; // Recipients first
      }
      // Then sort by name
      return getDisplayName(a).localeCompare(getDisplayName(b));
    });
  };

  if (!isAdmin) {
    return (
      <div className="mx-notifications-container">
        <div className="unauthorized-message">
          <p>No tens permís per gestionar les notificacions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-notifications-container">
      <h2>Configuració de notificacions de comandes</h2>
      <p className="notifications-description">
        Gestiona quins responsables rebran notificacions quan es creï una nova comanda de la botiga.
      </p>
      
      {saveStatus.message && (
        <div className={`status-message ${saveStatus.type}`}>
          {saveStatus.message}
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Carregant...</p>
        </div>
      ) : (
        <div className="notification-settings">
          <div className="unified-admin-list">
            <h3>Responsables de la botiga</h3>
            <p className="subtitle">Selecciona els responsables que rebran notificacions de noves comandes.</p>
            
            {getAllAdmins().length === 0 ? (
              <p className="empty-list-message">
                No s'han trobat responsables.
              </p>
            ) : (
              <ul className="admin-list">
                {getAllAdmins().map(admin => {
                  const isRecipient = admin.isRecipient;
                  
                  return (
                    <li key={admin.id || admin.user_id} className={`admin-item ${isRecipient ? 'is-recipient' : ''}`}>
                      <label className="admin-toggle">
                        <input
                          type="checkbox"
                          checked={isRecipient}
                          onChange={() => toggleRecipient(admin, isRecipient)}
                          disabled={loading}
                        />
                        <span className="admin-name">{getDisplayName(admin)}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            
            {recipients.length === 0 && (
              <p className="info-message">
                Si no selecciones cap destinatari, s'enviaran notificacions a tots els responsables.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="notification-info">
        <h3>Com funcionen les notificacions?</h3>
        <ul>
          <li>Cada vegada que es crea una nova comanda, els destinataris configurats rebran una notificació.</li>
          <li>Si no hi ha destinataris configurats, les notificacions s'enviaran a tots els responsables.</li>
          <li>Per rebre notificacions, l'usuari ha de tenir l'aplicació mòbil instal·lada i haver acceptat les notificacions.</li>
        </ul>
      </div>
    </div>
  );
}

export default MerxandatgeNotifications;
